import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import { colors, layering } from '@mobi/component-library/Foundations'
import { AppRoutes } from '@core/AppRoutes'
import { setBetSlipIsOpen } from '@core/Areas/AppHeader/Store'
import { FirstPostConfirmation } from '@core/Areas/BetLoop/Components/Common/FirstPostConfirmation'
import { ProfileAvatar } from '@core/Areas/BetLoop/Components/Common/ProfileAvatar'
import { trackBetLoopShareBetBlocked } from '@core/Areas/BetLoop/helpers/analytics'
import { isBetLoopProblemResponse } from '@core/Areas/BetLoop/helpers/api/typeGuards'
import { handleApiErrorNotification } from '@core/Areas/BetLoop/helpers/handleApiErrorNotification'
import { navigateToMyProfileWithViewBet } from '@core/Areas/BetLoop/helpers/navigation'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { useIsBetLoopEntitled } from '@core/Areas/BetLoop/hooks/useIsBetLoopEntitled'
import { useShouldAllowShareInBetLoop } from '@core/Areas/BetLoop/hooks/useShouldAllowShareInBetLoop'
import type { BetLoopProfile } from '@core/Areas/BetLoop/types'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { trackShareBetClick } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import {
  isBetSlipShareData,
  isKambiSportsShareData,
} from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/typeGuards'
import { extractShareContext, trackShareBetError } from '@core/Areas/ShareBet/helpers/analytics'
import type {
  BetSlipShare,
  KambiSportsShare,
  QuickbetShare,
  TicketShare,
} from '@core/Areas/ShareBet/types'
import { closeModal } from '@core/Components/Modal'
import { ShareOptionButton } from '../Common/ShareOptionButton'
import { postSharedBetToBetLoop } from './helpers/postSharedBetToBetLoop'

const enum LocalConstants {
  ShareToBetLoopSuccessToastId = 'share-to-bet-loop-success',
}

export const BetLoopShareButton: React.FC<{
  data: TicketShare | BetSlipShare | QuickbetShare | KambiSportsShare
  dismissTray: () => void
}> = ({ data, dismissTray }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isFirstPostModalOpen, setFirstPostModalOpen] = useState(false)

  const isEntitled = useIsBetLoopEntitled()
  const { data: profileData, isLoading: isLoadingProfile } = useQuery(betLoopPrincipleProfileQuery)

  const { mutate, isPending } = useMutation({
    mutationFn: postSharedBetToBetLoop,
    onSuccess: ({ results, successfulCount, failedCount }) => {
      if (successfulCount > 0) {
        const postId = results.find(result => result.success)?.postId
        const onToastViewButtonClick = () => {
          dispatch(closeModal({ id: QUICKBET_MODAL_ID }))
          dispatch(setBetSlipIsOpen(false))
          postId && navigateToMyProfileWithViewBet({ postId })
          removeToast({ id: LocalConstants.ShareToBetLoopSuccessToastId })
        }
        const title =
          successfulCount > 1
            ? `${successfulCount} Bets shared to Bet Loop`
            : `Your bet was shared to Bet Loop`

        const toastOptions: Parameters<typeof addToast>[0] = {
          theme: 'dark',
          icon: 'success',
          title,
          id: LocalConstants.ShareToBetLoopSuccessToastId,
          button: { text: 'View', onClick: onToastViewButtonClick },
        }
        isBetSlipShareData(data) && data.share.toastEmitter
          ? data.share.toastEmitter.emit('addToast', toastOptions)
          : addToast(toastOptions)
        queryClient.invalidateQueries(betLoopPrincipleProfileQuery)
        trackShareBetClick({ data, isCostShared: false, destination: 'Bet Loop' })
      } else if (failedCount) {
        const title = results.find(result => !!result.errorMessage)?.errorMessage
        title && addToast({ id: title, title, icon: 'error', theme: 'dark', isDismissable: true })
      }
    },
    onError: err => {
      const { location, betType } = extractShareContext(data)
      const message = isBetLoopProblemResponse(err)
        ? err.detail || err.title || 'Unknown error'
        : 'Unknown error'
      trackShareBetError({ message, destination: 'Bet Loop', location, betType })
      handleApiErrorNotification(err)
    },
  })

  const postToBetLoop = () => {
    if (profileData) {
      mutate(
        isKambiSportsShareData(data)
          ? { coupon: data.share.coupon }
          : { ticketSequence: data.share.ticketSequence as number }
      )
    } else {
      dispatch(setBetSlipIsOpen(false))
      navigate(AppRoutes.BetLoop)
    }
  }

  const handleShareToBetLoopClick = () => {
    if (profileData?.shareCount === 0) {
      setFirstPostModalOpen(true)
    } else {
      dismissTray()
      postToBetLoop()
    }
  }

  const handleFirstPostConfirm = () => {
    setFirstPostModalOpen(false)
    dismissTray()
    postToBetLoop()
  }

  const isShareAllowed = useShouldAllowShareInBetLoop(data)

  const handleBlockShareFromBetLoop = () => {
    addToast({
      id: 'addto-sharehub-blocked',
      isDismissable: true,
      title: !isEntitled
        ? 'You don’t currently have access to Bet Loop'
        : 'Your bet cannot be shared to Bet Loop',
      icon: 'warning',
      theme: 'dark',
    })
    trackBetLoopShareBetBlocked(data, isEntitled)
  }

  return (
    <>
      {isShareAllowed && isEntitled ? (
        <ShareOptionButton>
          <button
            onClick={handleShareToBetLoopClick}
            disabled={isLoadingProfile || isPending}
            type='button'
          >
            <BetLoopShareButtonContent profile={profileData} />
          </button>
        </ShareOptionButton>
      ) : (
        <ShareOptionButton isBlocked>
          <button onClick={handleBlockShareFromBetLoop} type='button'>
            <BetLoopShareButtonContent profile={profileData} isBlocked />
          </button>
        </ShareOptionButton>
      )}

      {createPortal(
        <FirstPostConfirmation
          isOpen={isFirstPostModalOpen}
          onConfirm={handleFirstPostConfirm}
          onCancel={() => setFirstPostModalOpen(false)}
          zIndex={layering.overlayHigh + 1}
        />,
        document.body
      )}
    </>
  )
}

// ===============
// Local Component
// ===============

const BetLoopShareButtonContent: React.FC<{ isBlocked?: boolean; profile?: BetLoopProfile }> = ({
  profile,
  isBlocked = false,
}) => (
  <>
    {profile ? (
      <div>
        <span>
          <ProfileAvatar avatar={profile.avatar} color={profile.color} size='sm' />
        </span>
      </div>
    ) : (
      <SvgLazy
        name='BetLoopHeaderLogo'
        width='7rem'
        color={isBlocked ? colors.neutral[500] : colors.lavender[750]}
      />
    )}
    Share to Bet Loop
  </>
)
